import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { showSnackbar } from '../../../api/snackbarSlice';
import { useDispatch, useSelector } from 'react-redux';
import { apiClient } from '../../../utils/axios';
import { AxiosError } from 'axios';
import { useTranslatedMessages } from '../../../hooks/useTranslatedMessages';

export const fetchSalesInvoice = async (query) => {
  const { data } = await apiClient.get(`/sales-invoices/${query.queryKey[1]}`);
  return data;
};

export const fetchPurchaseInvoice = async (query) => {
  const { data } = await apiClient.get(`/purchase-invoices/${query.queryKey[1]}`);
  return data;
};

const normalizeAttachment = (attachment = {}) => ({
  id: attachment.id ?? null,
  file_name: attachment.file_name ?? attachment.fileName ?? '',
  file_type: attachment.file_type ?? attachment.fileType ?? 'application/octet-stream',
});

const normalizeInvoice = (invoice = {}) => ({
  ...invoice,
  payment_means: normalizePaymentMeans(invoice.payment_means),
  attachments: Array.isArray(invoice.attachments) ? invoice.attachments.map(normalizeAttachment) : [],
});
const normalizePaymentMeans = (paymentMeans) => {
  if (!paymentMeans) return [];
  if (Array.isArray(paymentMeans)) return paymentMeans;
  if (typeof paymentMeans === 'object') return [paymentMeans];
  return [];
};

export const useGetInvoiceHandler = (id, isPurchase = false) => {
  const dispatch = useDispatch();
  const { eInvoiceForm } = useTranslatedMessages();
  const messages = eInvoiceForm?.invoice_handlers || {};
  const [invoice, setInvoice] = useState(undefined);

  const {
    data: fetchedData,
    isLoading,
    isFetching,
    isError,
    error,
  } = useQuery({
    queryKey: [isPurchase ? 'purchase-invoices' : 'sales-invoices', id], // A unique key for this query
    queryFn: isPurchase ? fetchPurchaseInvoice : fetchSalesInvoice,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchOnMount: 'always',
    retry: 3,
  });

  useEffect(() => {
    if (fetchedData) {
      setInvoice(normalizeInvoice(fetchedData));
    }
  }, [fetchedData]);

  useEffect(() => {
    if (isError) {
      const fallbackMessage = messages.load_invoice_error ?? 'Failed to load invoice';
      if (error instanceof AxiosError) {
        if (error.response?.status) {
          dispatch(
            showSnackbar({
              message: error.response?.data?.message ?? fallbackMessage,
              severity: 'error',
            }),
          );
        } else {
          dispatch(showSnackbar({ message: error.message ?? fallbackMessage, severity: 'error' }));
        }
      } else {
        dispatch(
          showSnackbar({
            message: error?.message ?? fallbackMessage,
            severity: 'error',
          }),
        );
      }
    }
  }, [dispatch, error, isError, messages.load_invoice_error]);

  return {
    invoice: invoice,
    isLoading,
  };
};
